/**
 * SsrFinderController — thin controller for the start-page combobox-stack finder.
 *
 * Rebuilt onto `TreeService` (Phase 6, SEC-6-3): the old
 * `ModelFinderCascade` + `core.replay(...)` restore is gone. The controller
 * builds a `TreeService` from `data-mf-subtree-hash`, fetches `/levels`, composes
 * `ModelFinderCore`, resolves the seed id (`datte_mf` URL > `data-preselect` >
 * localStorage) and `restore`s it behind the FOUC loading state. There is no
 * modal — restore runs once at construction.
 *
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.5
 */

import { ModelFinderCore } from './controller/model-finder-core';
import { resolveSeed, makeUrlWriter, MF_PARAM_SSR } from './controller/seed';
import { TreeService } from './data/tree-service';
import { fetchLevels } from './data/endpoint';
import { subtreeHash, levelsHash } from './domain/brand';
import { DropdownView } from './view/dropdown-view';
import type { ModelFinderView } from './view/view';

class SsrFinderController {
  private readonly root: HTMLElement;
  private readonly tree: TreeService;
  private readonly view: ModelFinderView;

  constructor(element: HTMLElement) {
    this.root = element;
    // Brand the page-attribute subtree stamp at the DOM boundary (ADR-0011 §5).
    this.tree = new TreeService(subtreeHash(element.dataset.mfSubtreeHash || ''));
    this.view = new DropdownView(element);
    // Same reasoning as ModelFinderCore's select handler: a failed boot leaves the
    // SSR finder blank; keep that UI, but make the cause visible.
    void this.boot().catch((error: unknown) => {
      console.error('[ModelFinderSSR] boot failed — the finder stays empty:', error);
    });
  }

  private async boot(): Promise<void> {
    const seed = resolveSeed(this.root, MF_PARAM_SSR);
    if (seed !== null) this.root.classList.add('datte-mf-loading');

    try {
      const levels = await fetchLevels(
        levelsHash(this.root.dataset.mfLevelsHash || ''),
        this.root.dataset.mfLang || ''
      );
      const core = new ModelFinderCore(this.tree, this.view, levels, makeUrlWriter(MF_PARAM_SSR));
      if (seed !== null) await core.restore(seed);
    } finally {
      // Always reveal — even on a partial restore or an endpoint error.
      this.root.classList.remove('datte-mf-loading');
      this.root.classList.add('datte-mf-initialized');
    }
  }
}

/** Initialize every SSR finder on the page. */
function initializeSsrFinder(): void {
  document.querySelectorAll<HTMLElement>('.datte-model-finder-ssr').forEach((container) => {
    if (container.dataset.initialized === 'true') return;
    new SsrFinderController(container);
    container.dataset.initialized = 'true';
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSsrFinder);
} else {
  initializeSsrFinder();
}

declare global {
  interface Window {
    SsrFinderController: typeof SsrFinderController;
    initializeSsrFinder: typeof initializeSsrFinder;
  }
}

if (typeof window !== 'undefined') {
  window.SsrFinderController = SsrFinderController;
  window.initializeSsrFinder = initializeSsrFinder;
}
